/**
 * Parse EBUScore CSV files (Teams and Pairs)
 */

import { calculateVPs } from './vpCalculator';
import { recalculatePairScores } from './pairsScoring';
import { mapPlayerNames } from './playerNameMapper';

export async function parseEBUScoreCSV(csvText) {
  const lines = csvText.split('\n').map(line => line.trim()).filter(line => line);
  
  const metadata = {};
  const scores = [];
  const travellers = {};
  
  let section = 'metadata';
  let currentBoard = null;
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    
    // Parse metadata (exclude #Scores, #Travellers, and #Board;X lines)
    if (line.startsWith('#') && !line.startsWith('#Scores') && !line.startsWith('#Travellers') && !line.startsWith('#Board;')) {
      const [key, ...valueParts] = line.substring(1).split(';');
      const value = valueParts.join(';').trim();
      
      if (key === 'Boardformat') continue;
      
      metadata[key] = value;
      continue;
    }
    
    // Section markers
    if (line === '#Scores') {
      section = 'scores';
      continue;
    }
    
    if (line === '#Travellers') {
      section = 'travellers';
      continue;
    }
    
    // Parse scores section
    if (section === 'scores' && !line.startsWith('#') && !line.startsWith('Position;')) {
      const parts = line.split(';');
      
      // Determine if Teams or Pairs based on headers
      if (metadata.Version.includes('Teams')) {
        scores.push({
          position: parts[0],
          team: parts[1],
          teamname: parts[2],
          players: [parts[3], parts[4], parts[5], parts[6], parts[7], parts[8], parts[9], parts[10]].filter(p => p),
          imps: parseFloat(parts[13]) || 0,  // Column 13 is Imps
          vps: 0,   // Will be recalculated - don't trust CSV value
        });
      } else {
        // Pairs
        scores.push({
          position: parts[0],
          pair: parts[1],
          players: [parts[2], parts[3]].filter(p => p),
          score: parseFloat(parts[5]) || 0,
          percent: parseFloat(parts[7]) || 0,
        });
      }
      continue;
    }
    
    // Parse travellers section
    if (section === 'travellers') {
      if (line.startsWith('#Board;')) {
        const parts = line.split(';');
        currentBoard = parts[1];
        travellers[currentBoard] = [];
        continue;
      }
      
      if (!line.startsWith('#') && !line.startsWith('NS;') && currentBoard) {
        const parts = line.split(';');
        let nsScore = parts[2] || '';
        let ewScore = parts[3] || '';
        
        // If one score is blank, it should be the negative of the other
        if (nsScore && !ewScore) {
          ewScore = nsScore.startsWith('-') ? nsScore.substring(1) : `-${nsScore}`;
        } else if (ewScore && !nsScore) {
          nsScore = ewScore.startsWith('-') ? ewScore.substring(1) : `-${ewScore}`;
        }
        
        travellers[currentBoard].push({
          ns: parts[0],
          ew: parts[1],
          nsScore: nsScore,
          ewScore: ewScore,
          nsPts: parseFloat(parts[4]) || 0,
          ewPts: parseFloat(parts[5]) || 0,
          contract: parts[6] || '',
          made: parts[7] || '',
          declarer: parts[8] || '',
          lead: parts[9] || '',
        });
      }
    }
  }
  
  // Apply name mapping to all player names in scores
  for (const score of scores) {
    if (score.players && Array.isArray(score.players)) {
      score.players = await mapPlayerNames(score.players);
    }
  }

  // Add player names to traveller results from scores
  const pairNamesMap = {};
  scores.forEach(score => {
    if (score.pair) {
      pairNamesMap[score.pair] = score.players.join(' - ');
    } else if (score.team) {
      // For teams, we'll handle this differently later
      pairNamesMap[score.team] = score.players.join(', ');
    }
  });

  // Attach player names to each traveller result
  Object.keys(travellers).forEach(boardNum => {
    travellers[boardNum].forEach(result => {
      if (result.ns && pairNamesMap[result.ns]) {
        result.nsPlayers = pairNamesMap[result.ns];
      }
      if (result.ew && pairNamesMap[result.ew]) {
        result.ewPlayers = pairNamesMap[result.ew];
      }
    });
  });
  
  // Recalculate VPs for teams using correct formula
  if (metadata.Version.includes('Teams')) {
    const numBoards = parseInt(metadata.Boards) || 0;
    
    if (numBoards === 0) {
      console.error('ERROR: Could not parse number of boards from CSV. VPs will not be calculated.');
      return {
        metadata,
        scores,
        travellers,
        gameType: metadata.Version.includes('Teams') ? 'teams' : 'pairs',
      };
    }
    
    // For 2-team match: calculate VPs once based on the winning team's margin
    if (scores.length === 2) {
      const team1 = scores[0];
      const team2 = scores[1];
      
      // Calculate VPs based on the margin
      const margin = team1.imps;
      const vpResult = calculateVPs(margin, numBoards);
      
      // Assign VPs
      team1.vps = vpResult.team1VP;
      team2.vps = vpResult.team2VP;
    } else {
      // Multi-team event: calculate individually
      scores.forEach(score => {
        const vpResult = calculateVPs(score.imps, numBoards);
        if (score.imps > 0) {
          score.vps = vpResult.team1VP;
        } else if (score.imps < 0) {
          score.vps = vpResult.team2VP;
        } else {
          score.vps = 10;
        }
      });
    }
  }
  
  // Recalculate pairs percentages from travellers
  if (metadata.Version.includes('Pairs') && Object.keys(travellers).length > 0) {
    const recalculatedScores = recalculatePairScores(travellers);
    
    // Check if we have enough data for recalculation
    if (recalculatedScores.length > 0) {
      // Merge with existing scores to preserve player names
      scores.forEach(score => {
        const recalc = recalculatedScores.find(r => r.pair === score.pair);
        if (recalc) {
          score.score = recalc.totalMPs;
          score.percent = recalc.percentage;
          score.position = recalc.position.toString();
        }
      });
    }
  }
  
  return {
    metadata,
    scores,
    travellers,
    gameType: metadata.Version.includes('Teams') ? 'teams' : 'pairs',
  };
}

export function formatContract(contract, made, declarer) {
  if (!contract) return 'Passed out';
  
  const tricks = parseInt(made) || 0;
  const level = contract.charAt(0);
  const expectedTricks = 6 + parseInt(level);
  
  let result = contract;
  
  if (tricks > 0) {
    if (tricks === expectedTricks) {
      result += '=';
    } else if (tricks > expectedTricks) {
      result += '+' + (tricks - expectedTricks);
    } else {
      result += '-' + (expectedTricks - tricks);
    }
  }
  
  return result;
}
